/* eslint-disable no-console */
import { Ability } from '@casl/ability';
import {
  Actions,
  ResultStatus,
  Subjects,
  TaskTemplate,
} from '@dispatch/shared/types';
import { useTableState } from '@dispatch/shared/hooks';
import {
  activeColumn,
  boldColumn,
  CreateNewEntityButton,
  dateColumn,
  linkColumn,
  PageComponent,
  TableComponent,
  TableProps,
  textColumn,
} from '@dispatch/web/ui';
import styled from '@emotion/styled';
import { GridColDef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface TaskTemplatesProps {
  rows?: TaskTemplate[];
  status: ResultStatus;
  permissions: Ability<[Actions, Subjects]> | undefined;
}

const StyledTaskTemplates = styled.div`
  width: 100%;
`;

const LinkText = styled.span`
  font-weight: 400;
  color: #000000;
  &:hover {
    color: lightgrey;
  }
`;

// A shared empty default: a fresh [] on every render would re-run the effect
// that copies rows into state, so a loading page would re-render forever.
const NO_ROWS: TaskTemplate[] = [];

export function TaskTemplates({
  rows = NO_ROWS,
  status,
  permissions,
}: TaskTemplatesProps) {
  const { tableState, setTableState } = useTableState();
  const [data, setData] = useState<TaskTemplate[] | undefined>([]);
  const [searchText, setSearchText] = useState<string>('');
  const navigate = useNavigate();
  useEffect(() => {
    setData([...rows]);
  }, [rows]);

  const columns: GridColDef[] = [
    linkColumn<TaskTemplate, string>(
      {
        field: 'externalId',
        headerName: 'Number',
        sortable: false,
      },
      (taskTemplate, _rowValue) =>
        `/admin/task-templates/${taskTemplate?.externalId}`
    ),
    boldColumn({
      field: 'name',
      headerName: 'Name',
      sortable: false,
    }),
    textColumn({
      field: 'description',
      headerName: 'Description',
      sortable: false,
      flex: 2,
    }),
    dateColumn(
      {
        field: 'dateCreated',
        headerName: 'Date Created',
        sortable: false,
      },
      'MMM dd, yyy'
    ),
    dateColumn(
      {
        field: 'dateEdited',
        headerName: 'Date Edited',
        sortable: false,
      },
      'MMM dd, yyyy'
    ),
    textColumn({
      field: 'timesUsed',
      headerName: 'Times Used',
      sortable: false,
    }),
    activeColumn({
      field: 'isActive',
      headerName: 'Active',
      sortable: false,
    }),
  ];

  const onSearch = (value: string) => {
    setSearchText(value);
    const dataClone = [...rows];
    if (value) {
      const filterData = dataClone.filter((row: TaskTemplate) =>
        row.name?.includes(value)
      );
      return setData(filterData);
    }

    setData(dataClone);
  };

  const tableArgs: TableProps<TaskTemplate> = {
    tableState: {
      columnModel: tableState['taskTemplates'].columnModel,
      pinnedColumns: {
        ...tableState['taskTemplates'].pinnedColumns,
      },
    },
    handleShowColumns: (newModel) => {
      if (!newModel) return;
      setTableState({
        ...tableState,
        taskTemplates: {
          ...tableState['taskTemplates'],
          columnModel: newModel,
        },
      });
    },
    handlePinnedColumns: (newPinned) => {
      setTableState({
        ...tableState,
        taskTemplates: {
          ...tableState['taskTemplates'],
          pinnedColumns: newPinned,
        },
      });
    },
    rows: data || [],
    columns,
    emptyEntityName: 'Task Template',
    status: searchText !== '' && data && data.length <= 0 ? 'noresult' : status,
  };

  return (
    <PageComponent
      title="Task Templates"
      onSearch={onSearch}
      actions={
        permissions?.can?.('set', 'task-template') && (
          <CreateNewEntityButton
            onClick={() => navigate('/admin/task-templates/add')}
            entityName="Task Template"
          />
        )
      }
    >
      <StyledTaskTemplates>
        <TableComponent {...tableArgs} />
      </StyledTaskTemplates>
    </PageComponent>
  );
}

export default TaskTemplates;
